/**
 * Reads user identity + session + browser context from the page. Identity
 * is a best-effort decode of the JWT in `localStorage.token` (payload-only,
 * no signature verification — backend services re-verify the token before
 * honoring the user; we're only stamping what the browser already trusts).
 *
 * Even unauthenticated frontends (app-login, app-404, app-home) get useful
 * context: anonymous id, language, viewport, timezone, network class,
 * dark mode. That's what makes a span debuggable when something goes
 * wrong before the user has signed in.
 *
 * Safe to call from any context. Missing/expired/garbled tokens just
 * yield `undefined` fields; never throws.
 *
 * Module references the browser globals through `window` so it doesn't
 * trip CRA's `no-restricted-globals` lint (see app-404 build failure).
 */

export interface UserContext {
  /** Authenticated user id. Tries `sub`, then `id`, `user_id`, `uid`, `userId`. */
  userId?: string;
  email?: string;
  /** Display name. Tries `name`, `preferred_username`, `display_name`, `username`, `nickname`, then `given_name + family_name`. */
  name?: string;
  /** OAuth-style scopes flattened to a single space-delimited string. Pulls from `scope`, `scopes`, `roles`, `permissions`, `groups`. */
  scopes?: string;
  /** Tenant / workspace / org claim. Tries `tenant`, `workspace`, `org`, `organization`, `tid`. */
  tenant?: string;
  /** JWT `iss` claim — useful when multiple identity providers are in play. */
  issuer?: string;
  /** JWT `jti` claim — unique token id for debugging "wrong token" issues. */
  tokenId?: string;
  /** JWT `iat` (issued at, UNIX seconds). */
  issuedAt?: number;
  /** Whether the JWT exp is in the past. */
  expired?: boolean;
  /** UNIX seconds. */
  exp?: number;
  /** Persistent anonymous id from the analytics layer. */
  anonymousId?: string;
  /** Current SPA route — `location.pathname + location.search`. */
  route?: string;
  /** document.title at the moment of the request. */
  pageTitle?: string;
  /** "1440x900" — the layout viewport at request time. */
  viewport?: string;
  /** navigator.language, e.g. "en-US". */
  language?: string;
  /** localStorage.i18nextLng (whatever the SPA i18n layer picked). */
  i18nLanguage?: string;
  /** Browser-reported timezone, e.g. "Europe/Vienna". */
  timezone?: string;
  /** "light" | "dark" — derived from localStorage.darkMode or media query. */
  colorScheme?: string;
  /** navigator.connection.effectiveType: "4g", "3g", etc. */
  connectionType?: string;
  /** Boolean from navigator.onLine. */
  online?: boolean;
}

function base64UrlDecode(segment: string): string | undefined {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/') +
      '==='.slice((segment.length + 3) % 4);
    if (typeof atob === 'undefined') return undefined;
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return undefined;
  }
}

type JwtPayload = Record<string, unknown>;

function decodeJwtPayload(token: string): JwtPayload | undefined {
  const parts = token.split('.');
  if (parts.length < 2) return undefined;
  const json = base64UrlDecode(parts[1]);
  if (!json) return undefined;
  try {
    return JSON.parse(json) as JwtPayload;
  } catch {
    return undefined;
  }
}

function readLocalStorage(key: string): string | undefined {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return undefined;
    return window.localStorage.getItem(key) || undefined;
  } catch {
    // Safari ITP / private mode can throw on access.
    return undefined;
  }
}

function flattenScopes(value: unknown): string | undefined {
  if (Array.isArray(value)) return value.filter(Boolean).map(String).join(' ') || undefined;
  if (typeof value === 'string') return value || undefined;
  return undefined;
}

function pickString(payload: JwtPayload, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}

function pickNumber(payload: JwtPayload, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = payload[k];
    if (typeof v === 'number') return v;
  }
  return undefined;
}

/**
 * Merge multiple OAuth-style scope/permission claims into one space-delimited
 * string. Different IdPs put authorization data under different names; we
 * include them all so a single Tempo attribute filter catches everything.
 */
function mergeScopes(payload: JwtPayload): string | undefined {
  const parts: string[] = [];
  for (const key of ['scope', 'scopes', 'roles', 'permissions', 'groups']) {
    const flat = flattenScopes(payload[key]);
    if (flat) parts.push(flat);
  }
  return parts.length ? Array.from(new Set(parts.join(' ').split(/\s+/).filter(Boolean))).join(' ') : undefined;
}

function detectColorScheme(): string | undefined {
  const stored = readLocalStorage('darkMode');
  if (stored === 'true') return 'dark';
  if (stored === 'false') return 'light';
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function detectConnectionType(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  // `connection` is non-standard (Chromium / WebView) — type-only access.
  const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
  return conn?.effectiveType;
}

/**
 * Snapshot the current user/session/browser context. Read fresh each call
 * — token may rotate, viewport may resize, network may flap.
 */
export function getUserContext(): UserContext {
  const ctx: UserContext = {};

  if (typeof window !== 'undefined') {
    if (window.location) ctx.route = window.location.pathname + window.location.search;
    if (window.document) ctx.pageTitle = window.document.title || undefined;
    if (typeof window.innerWidth === 'number' && typeof window.innerHeight === 'number') {
      ctx.viewport = `${window.innerWidth}x${window.innerHeight}`;
    }
  }

  if (typeof navigator !== 'undefined') {
    ctx.language = navigator.language || undefined;
    ctx.online = typeof navigator.onLine === 'boolean' ? navigator.onLine : undefined;
  }

  try {
    ctx.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    /* unsupported — leave undefined */
  }

  ctx.colorScheme = detectColorScheme();
  ctx.connectionType = detectConnectionType();
  ctx.i18nLanguage = readLocalStorage('i18nextLng');
  ctx.anonymousId = readLocalStorage('cs.analytics.anonymousId');

  const token = readLocalStorage('token');
  if (!token) return ctx;

  const payload = decodeJwtPayload(token);
  if (!payload) return ctx;

  ctx.userId = pickString(payload, ['sub', 'id', 'user_id', 'uid', 'userId']);
  ctx.email = pickString(payload, ['email', 'mail', 'email_address']);

  let name = pickString(payload, [
    'name',
    'preferred_username',
    'display_name',
    'username',
    'nickname',
  ]);
  if (!name) {
    const given = pickString(payload, ['given_name', 'first_name']);
    const family = pickString(payload, ['family_name', 'last_name']);
    if (given || family) name = `${given ?? ''} ${family ?? ''}`.trim() || undefined;
  }
  ctx.name = name;

  ctx.scopes = mergeScopes(payload);
  ctx.tenant = pickString(payload, ['tenant', 'workspace', 'org', 'organization', 'tid']);
  ctx.issuer = pickString(payload, ['iss']);
  ctx.tokenId = pickString(payload, ['jti']);
  ctx.issuedAt = pickNumber(payload, ['iat']);

  const exp = pickNumber(payload, ['exp']);
  if (typeof exp === 'number') {
    ctx.exp = exp;
    ctx.expired = exp * 1000 < Date.now();
  }

  return ctx;
}
